import { BulletinMediaItem } from "./types";

/**
 * Where a dragged file lands, and the one `sort_ordinal` that has to be told to the server.
 *
 * The order lives server-side as a float per file (uploads take `max + 1`), so a drop is **one file's
 * new ordinal**, not a renumbering of the list: dropping between two files takes the midpoint of
 * their ordinals, and dropping past either end takes one step beyond it. That is the old flow's
 * `SortableAttachments` arithmetic, kept identical on purpose — the same column, read by the same
 * board card, and a second scheme in the same table would be a bug waiting for the first bulletin
 * that had been reordered in both flows.
 *
 * The halving is not infinite: about fifty drops into the same gap and the midpoints stop being
 * distinguishable as doubles. Renumbering server-side is the fix if that ever shows up; it never has
 * in the old flow, and a bulletin holds a handful of files.
 */

export interface MediaReorder {
  /** The list in its new order — already sorted, so a caller can write it straight in. */
  items: BulletinMediaItem[];
  /** The only file whose ordinal changed. */
  moved: { filename: string; sortOrdinal: number };
}

/** `null` when there is nothing to do: a drop on itself, or on something no longer in the list. */
export const reorderMedia = (
  items: BulletinMediaItem[],
  activeId: string,
  overId: string,
): MediaReorder | null => {
  const from = items.findIndex((item) => item.id === activeId);
  const to = items.findIndex((item) => item.id === overId);
  if (from === -1 || to === -1 || from === to) return null;

  const target = items[to];
  const forwards = to > from;
  const neighbour = items[forwards ? to + 1 : to - 1];

  const sortOrdinal = neighbour
    ? (target.sortOrdinal + neighbour.sortOrdinal) / 2
    : // Dropped past the end of the list, where there is no neighbour to split the difference with.
      target.sortOrdinal + (forwards ? 1 : -1);

  const moved = { ...items[from], sortOrdinal };

  return {
    items: items
      .map((item) => (item.id === activeId ? moved : item))
      .sort((a, b) => a.sortOrdinal - b.sortOrdinal),
    moved: { filename: moved.filename, sortOrdinal },
  };
};
